import { supabase } from "./supabase";
import type { PlantAnalysis } from "./claude";
import { uploadPlantImage } from "./storage";

// Mirror of src/lib/plants.ts in the RN project. `savePlant` is the one
// meaningful divergence: web takes an explicit `householdId` (no fallback
// to households[0]) and a `File` instead of a string URI.

export interface Plant {
  id: string;
  household_id: string | null;
  common_name: string;
  scientific_name: string;
  plant_type: string;
  pot_size: string;
  plant_size: string;
  health_status: string;
  diseases: string[];
  care_suggestions: string[];
  watering_frequency_days: number;
  watering_amount: string;
  watering_notes: string;
  light_needs: string;
  next_watering: string;
  image_url: string | null;
  nickname: string | null;
  created_at: string;
}

export async function fetchPlants(): Promise<Plant[]> {
  const { data, error } = await supabase
    .from("plants")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function fetchPlant(id: string): Promise<Plant> {
  const { data, error } = await supabase
    .from("plants")
    .select("*")
    .eq("id", id)
    .single();
  if (error) throw error;
  return data;
}

export async function savePlant(
  analysis: PlantAnalysis,
  nickname: string | null,
  imageFile: File | null,
  householdId: string,
  lastWateredDate?: Date
): Promise<Plant> {
  // Upload image if present — fail-soft: missing image still saves the
  // plant, same policy as the RN app.
  let persistedImageUrl: string | null = null;
  if (imageFile) {
    try {
      persistedImageUrl = await uploadPlantImage(imageFile);
    } catch (err) {
      console.error("[SPRIG WEB] plant image upload failed:", err);
      persistedImageUrl = null;
    }
  }

  const { data, error } = await supabase
    .from("plants")
    .insert({
      household_id: householdId,
      common_name: analysis.commonName,
      scientific_name: analysis.scientificName,
      plant_type: analysis.plantType,
      pot_size: analysis.potSize,
      plant_size: analysis.plantSize,
      health_status: analysis.healthStatus,
      diseases: analysis.diseases,
      care_suggestions: analysis.careSuggestions,
      watering_frequency_days: analysis.wateringSchedule.frequencyDays,
      watering_amount: analysis.wateringSchedule.amount,
      watering_notes: analysis.wateringSchedule.notes,
      light_needs: analysis.lightNeeds,
      next_watering: new Date(
        (lastWateredDate ? lastWateredDate.getTime() : Date.now()) +
          analysis.wateringSchedule.frequencyDays * 24 * 60 * 60 * 1000
      ).toISOString(),
      image_url: persistedImageUrl,
      nickname: nickname || null,
    })
    .select()
    .single();
  if (error) throw error;

  // Record the initial watering event so "last watered" is accurate
  if (lastWateredDate) {
    const { data: auth } = await supabase.auth.getUser();
    if (auth.user) {
      await supabase.from("watering_events").insert({
        plant_id: data.id,
        user_id: auth.user.id,
        watered_at: lastWateredDate.toISOString(),
      });
    }
  }

  return data;
}

export async function countPlantsInHousehold(
  householdId: string
): Promise<number> {
  const { count, error } = await supabase
    .from("plants")
    .select("*", { count: "exact", head: true })
    .eq("household_id", householdId);
  if (error) throw error;
  return count || 0;
}

export interface PlantUpdates {
  nickname?: string | null;
  watering_frequency_days?: number;
  watering_amount?: string;
  watering_notes?: string;
  light_needs?: string;
}

export async function updatePlant(
  id: string,
  updates: PlantUpdates
): Promise<Plant> {
  const { data, error } = await supabase
    .from("plants")
    .update(updates)
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deletePlant(id: string): Promise<void> {
  const { error } = await supabase.from("plants").delete().eq("id", id);
  if (error) throw error;
}

export async function markAsWatered(id: string): Promise<Plant> {
  const current = await fetchPlant(id);
  const now = new Date();
  const nextWatering = new Date(
    now.getTime() + current.watering_frequency_days * 24 * 60 * 60 * 1000
  ).toISOString();

  const { data: auth } = await supabase.auth.getUser();
  if (auth.user) {
    await supabase.from("watering_events").insert({
      plant_id: id,
      user_id: auth.user.id,
      watered_at: now.toISOString(),
    });
  }

  const { data, error } = await supabase
    .from("plants")
    .update({ next_watering: nextWatering })
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export interface WateringEvent {
  id: string;
  plant_id: string;
  user_id: string;
  watered_at: string;
  notes: string | null;
}

export async function fetchLastWatered(
  plantId: string
): Promise<WateringEvent | null> {
  const { data, error } = await supabase
    .from("watering_events")
    .select("*")
    .eq("plant_id", plantId)
    .order("watered_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function deleteWateringEvent(
  eventId: string,
  plantId: string
): Promise<Plant> {
  await supabase.from("watering_events").delete().eq("id", eventId);
  // Recalculate next_watering from the new latest event
  const plant = await fetchPlant(plantId);
  const newLast = await fetchLastWatered(plantId);
  const baseTime = newLast
    ? new Date(newLast.watered_at).getTime()
    : Date.now();
  const nextWatering = new Date(
    baseTime + plant.watering_frequency_days * 24 * 60 * 60 * 1000
  ).toISOString();
  const { data, error } = await supabase
    .from("plants")
    .update({ next_watering: nextWatering })
    .eq("id", plantId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateWateringEvent(
  eventId: string,
  plantId: string,
  newDate: Date
): Promise<Plant> {
  await supabase
    .from("watering_events")
    .update({ watered_at: newDate.toISOString() })
    .eq("id", eventId);
  // Recalculate next_watering
  const plant = await fetchPlant(plantId);
  const nextWatering = new Date(
    newDate.getTime() + plant.watering_frequency_days * 24 * 60 * 60 * 1000
  ).toISOString();
  const { data, error } = await supabase
    .from("plants")
    .update({ next_watering: nextWatering })
    .eq("id", plantId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateWateringSchedule(
  id: string,
  frequencyDays: number
): Promise<Plant> {
  const lastEvent = await fetchLastWatered(id);
  const baseTime = lastEvent
    ? new Date(lastEvent.watered_at).getTime()
    : Date.now();
  const nextWatering = new Date(
    baseTime + frequencyDays * 24 * 60 * 60 * 1000
  ).toISOString();

  const { data, error } = await supabase
    .from("plants")
    .update({
      watering_frequency_days: frequencyDays,
      next_watering: nextWatering,
    })
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ---------- Health Checkups ----------

export interface HealthCheckup {
  id: string;
  plant_id: string;
  user_id: string;
  image_url: string;
  health_status: string;
  diseases: string[];
  care_suggestions: string[];
  comparison_note: string | null;
  confidence: number | null;
  checked_at: string;
}

export async function fetchHealthCheckups(
  plantId: string
): Promise<HealthCheckup[]> {
  const { data, error } = await supabase
    .from("health_checkups")
    .select("*")
    .eq("plant_id", plantId)
    .order("checked_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function saveHealthCheckup(
  plantId: string,
  imageUrl: string,
  result: {
    healthStatus: string;
    diseases: string[];
    careSuggestions: string[];
    comparisonNote: string;
    confidence: number;
  }
): Promise<HealthCheckup> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) throw new Error("Not authenticated");

  const { data, error } = await supabase
    .from("health_checkups")
    .insert({
      plant_id: plantId,
      user_id: auth.user.id,
      image_url: imageUrl,
      health_status: result.healthStatus,
      diseases: result.diseases,
      care_suggestions: result.careSuggestions,
      comparison_note: result.comparisonNote,
      confidence: result.confidence,
    })
    .select()
    .single();
  if (error) throw error;

  // Update the plant's current health fields + image
  await supabase
    .from("plants")
    .update({
      health_status: result.healthStatus,
      diseases: result.diseases,
      care_suggestions: result.careSuggestions,
      image_url: imageUrl,
    })
    .eq("id", plantId);

  return data;
}
